import type { Structure, VariantsDataMap } from '@wix/thunderbolt-becky-types'
import {
	Component,
	GridItemLayout,
	LayoutData,
	SingleLayoutData,
	SingleLayoutItem,
	UnresolvedBreakpointsData,
	BreakpointRange,
	UnitSize,
	GridContainerLayout,
	ResponsiveGridArea,
	MinMaxSize,
	Padding,
} from '@wix/thunderbolt-becky-types'
import { CompIdToBpToDimensions, BpToDimensions, DimensionsMap } from '../types/becky'

type ModelQueries = Array<string>
type BpIdToRowStartToComp = { [bp: string]: { [compId: string]: SingleLayoutItem } }

const removeHash = (value: string) => value && value.replace('#', '')
const screenHeight = 1000

const DEFAULT_BREAKPOINT_KEY = ''

const getBpIdToLayoutData = (layoutData: LayoutData, variantsData: VariantsDataMap, layouts: ModelQueries) =>
	layouts.reduce((acc: { [bp: string]: SingleLayoutItem }, layoutPointer) => {
		const layoutValue = layoutData[removeHash(layoutPointer)]
		if (layoutValue.type !== 'VariantRelation') {
			acc[DEFAULT_BREAKPOINT_KEY] = layoutValue
		} else {
			const layoutVariants: ModelQueries = layoutValue.variants
			const variantType = variantsData[removeHash(layoutVariants[0])]?.type
			const isDefaultLayoutInBp = layoutVariants.length === 1 && variantType === 'BreakpointRange'

			if (isDefaultLayoutInBp) {
				acc[layoutVariants[0]!] = layoutData[removeHash(layoutValue.to)]
			}
		}
		return acc
	}, {})

const getCompLayoutsPerBpId = (layoutData: LayoutData, variantsData: VariantsDataMap, comp: Component) => {
	const compLayout = layoutData[comp.layoutQuery!]

	if (!compLayout) {
		return {}
	}
	if (compLayout.type !== 'RefArray') {
		return { [DEFAULT_BREAKPOINT_KEY]: compLayout }
	}

	return getBpIdToLayoutData(layoutData, variantsData, compLayout.values)
}

const getBreakpointOrder = (pageComponent: Component, variantsData: VariantsDataMap) => {
	const breakpointsOrder = ['']
	if (!pageComponent?.breakpointVariantsQuery) {
		return breakpointsOrder
	}
	const breakpointQuery = removeHash(pageComponent.breakpointVariantsQuery)
	return breakpointsOrder
		.concat(((variantsData[breakpointQuery] as unknown) as UnresolvedBreakpointsData).values)
		.map((bpId) => removeHash(bpId))
}

const getCascadedLayout = (
	breakpointOrder: Array<string>,
	cascadedBpIndex: number,
	compDefaultLayoutPerBp: { [bp: string]: SingleLayoutItem }
) => {
	for (let bpIndex = cascadedBpIndex; bpIndex >= 0; bpIndex--) {
		const bpId = breakpointOrder[bpIndex]
		const layoutInBp = compDefaultLayoutPerBp[`#${bpId}`] || compDefaultLayoutPerBp[bpId]
		if (layoutInBp) {
			return layoutInBp
		}
	}
}

const getStructureLayoutData = (
	structure: Structure,
	pageId: string,
	breakpointOrder: Array<string>,
	layoutData: LayoutData,
	variantsData: VariantsDataMap
) => {
	return Object.keys(structure).reduce((accBpIdToCompToLayout: BpIdToRowStartToComp, compId: string) => {
		const comp = structure[compId]

		const compDefaultLayoutPerBp = getCompLayoutsPerBpId(layoutData, variantsData, comp)

		breakpointOrder.forEach((bp, bpIndex) => {
			const cascadedLayout = getCascadedLayout(breakpointOrder, bpIndex, compDefaultLayoutPerBp)
			if (cascadedLayout) {
				accBpIdToCompToLayout[bp] = accBpIdToCompToLayout[bp] || {}
				accBpIdToCompToLayout[bp][compId] = cascadedLayout
			}
		})

		return accBpIdToCompToLayout
	}, {})
}

const defaultBpAssumption = {
	value: 1920,
	type: 'px',
}
export const getCompsDimensionsHypothesis = (
	structure: Structure,
	pageId: string,
	layoutData: LayoutData,
	variantsData: VariantsDataMap
) => {
	const breakpointPageOrder = getBreakpointOrder(structure[pageId], variantsData)

	const pageLayoutData: BpIdToRowStartToComp = getStructureLayoutData(
		structure,
		pageId,
		breakpointPageOrder,
		layoutData,
		variantsData
	)

	const pageDimensions = calculateDimensionsPerBreakpoint(
		structure,
		breakpointPageOrder,
		pageLayoutData,
		pageId,
		variantsData
	)

	return pageDimensions
}
function calculateDimensionsPerBreakpoint(
	structure: Structure,
	breakpointOrder: Array<string>,
	layoutDataPerBp: BpIdToRowStartToComp,
	pageId: string,
	variantsData: VariantsDataMap
) {
	const dimensionsPerBp = breakpointOrder.map((bp) => {
		const viewPortWidth = (variantsData[bp] as BreakpointRange)?.max || defaultBpAssumption.value

		return calculateDimensions(structure[pageId], layoutDataPerBp[bp] || {}, structure, viewPortWidth)
	})

	const compIds = Object.keys(structure)
	return compIds.reduce((compIdToDimensionPerBpAcc: CompIdToBpToDimensions, compId) => {
		if (dimensionsPerBp[0][compId]) {
			compIdToDimensionPerBpAcc[compId] = breakpointOrder.reduce(
				(bpToDimensionAcc: BpToDimensions, breakpointId, bpIndex) => {
					if (dimensionsPerBp[bpIndex][compId]) {
						const { width = 0, height = 0 } = dimensionsPerBp[bpIndex][compId]
						bpToDimensionAcc[breakpointId] = { width: Math.floor(width), height: Math.floor(height) }
					}
					return bpToDimensionAcc
				},
				{}
			)
		}
		return compIdToDimensionPerBpAcc
	}, {})
}

function calculateDimensions(
	component: Component,
	layoutData: LayoutData,
	structure: Structure,
	viewPortWidth: number
) {
	if (!component) {
		return {}
	}
	let calculatedDimensions: DimensionsMap = {
		[component.id]: { width: viewPortWidth },
	}
	const queue = component.components?.map((compId) => ({ id: compId, parentId: component.id })) || []
	while (queue.length) {
		const currComp = queue.shift()!
		if (structure[currComp.id]) {
			calculatedDimensions = calculateWidth(
				structure[currComp.id],
				layoutData,
				structure,
				calculatedDimensions,
				currComp.parentId
			)
			calculatedDimensions = calculateHeight(
				structure[currComp.id],
				layoutData,
				structure,
				calculatedDimensions,
				currComp.parentId,
				viewPortWidth
			)
			structure[currComp.id].components?.forEach((compId) => {
				queue.push({ id: compId, parentId: currComp.id })
			})
		}
	}
	return calculatedDimensions
}

function getAvailableInnerWidth(
	compId: string,
	parentId: string,
	width: number,
	layoutData: LayoutData,
	calculatedDimensions: DimensionsMap
) {
	const compLayout = layoutData[compId] as SingleLayoutData
	if (!compLayout) {
		return calculatedDimensions[parentId].availableInnerWidth || calculatedDimensions[parentId].width
	}

	const remainingWidth = width
	const padding =
		compLayout.containerLayout?.type !== 'MeshContainerLayout'
			? compLayout.containerLayout?.padding
			: ({} as Padding)
	const left = (padding?.left as UnitSize)?.value || 0
	const right = (padding?.right as UnitSize)?.value || 0

	const result = remainingWidth - left - right
	return result > 0 ? result : 0
}

function getAvailableInnerHeight(
	compId: string,
	parentId: string,
	height: number,
	layoutData: LayoutData,
	calculatedDimensions: DimensionsMap
) {
	const compLayout = layoutData[compId] as SingleLayoutData
	if (!compLayout) {
		return calculatedDimensions[parentId].availableInnerHeight || calculatedDimensions[parentId].height
	}

	const remainingHeight = height
	const padding =
		compLayout.containerLayout?.type !== 'MeshContainerLayout'
			? compLayout.containerLayout?.padding
			: ({} as Padding)
	const top = (padding?.top as UnitSize)?.value || 0
	const bottom = (padding?.bottom as UnitSize)?.value || 0

	const result = remainingHeight - top - bottom
	return result > 0 ? result : 0
}

function calculateWidth(
	component: Component,
	layoutData: LayoutData,
	componentsMap: Structure,
	calculatedDimensions: DimensionsMap,
	parentId: string,
	lastKnownWidth?: number
) {
	const parentWidth =
		calculatedDimensions[parentId].availableInnerWidth || lastKnownWidth || calculatedDimensions[parentId].width

	if (calculatedDimensions[component.id]?.width) {
		return calculatedDimensions
	}
	calculatedDimensions[component.id] = calculatedDimensions[component.id] || {}

	let width = 0

	const currentLayoutData = layoutData[component.id] as SingleLayoutData
	const compWidth = currentLayoutData?.componentLayout?.width
	const isGrid = currentLayoutData?.itemLayout?.type === 'GridItemLayout'

	if (!currentLayoutData || (!compWidth && !isGrid)) {
		width = parentWidth || 0
	} else if (compWidth?.type === 'px') {
		width = compWidth.value
	}
	// If the component uses grid layout, calculate its width based on the grid settings todo what if it has siblings?
	else if (isGrid) {
		const itemLayout = currentLayoutData?.itemLayout as GridItemLayout
		const currGridArea = itemLayout.gridArea || {}
		const containerLayoutData = layoutData[parentId.replace('_relative', '')] as SingleLayoutData
		const containerLayout = containerLayoutData?.containerLayout as GridContainerLayout
		const columnsArray = containerLayout?.columns

		// todo if there is a sibling with pixels, should calculate it
		const { columnStart } = currGridArea as ResponsiveGridArea
		const availableWidth = parentWidth || 0
		const widthByContainer = columnsArray?.[(columnStart as number) - 1]

		width = availableWidth
		if (widthByContainer) {
			const availableWidthUnits = columnsArray?.reduce?.(
				(acc, columnUnit) => acc + (columnUnit as UnitSize).value,
				0
			)
			if (widthByContainer.type === 'fr') {
				width = availableWidth * (widthByContainer.value / availableWidthUnits)
				if (compWidth?.type === 'percentage') {
					width = ((width || 0) * compWidth.value) / 100
				}
			} else {
				width = availableWidth
			}
		}
	} else if (compWidth?.type === 'percentage') {
		width = ((parentWidth || 0) * compWidth.value) / 100
	} else {
		for (const childId of component.components || []) {
			const childComponent = componentsMap[childId]
			calculatedDimensions = calculateWidth(
				childComponent,
				layoutData,
				componentsMap,
				calculatedDimensions,
				component.id,
				parentWidth
			)
			width += calculatedDimensions[childId].width || 0
		}
	}

	const minWidth = currentLayoutData?.componentLayout?.minWidth
	if (minWidth?.type === 'px' && minWidth?.value > width) {
		width = minWidth?.value
	}

	calculatedDimensions[component.id].width = width
	calculatedDimensions[component.id].availableInnerWidth = getAvailableInnerWidth(
		component.id,
		parentId,
		width,
		layoutData,
		calculatedDimensions
	)

	return calculatedDimensions
}

function calculateHeight(
	component: Component,
	layoutData: LayoutData,
	componentsMap: Structure,
	calculatedDimensions: DimensionsMap,
	parentId: string,
	viewPortWidth: number,
	lastKnownHeight?: number
) {
	const parentHeight: number =
		calculatedDimensions[parentId].availableInnerHeight ||
		lastKnownHeight ||
		calculatedDimensions[parentId].height ||
		0

	if (calculatedDimensions[component.id]?.height) {
		return calculatedDimensions
	}
	calculatedDimensions[component.id] = calculatedDimensions[component.id] || {}

	let height = 0

	const currentLayoutData = layoutData[component.id] as SingleLayoutData
	// const componentLayout = currentLayoutData?.componentLayout as ComponentLayout
	const compHeight = currentLayoutData?.componentLayout?.height
	const isGrid = currentLayoutData?.itemLayout?.type === 'GridItemLayout'

	if (!currentLayoutData || (!compHeight && !isGrid)) {
		height = parentHeight
	} else if (compHeight?.type === 'px') {
		height = compHeight.value
	} else if (compHeight?.type === 'vw') {
		height = viewPortWidth * (compHeight?.value / 100)
	} else if (compHeight?.type === 'vh') {
		height = screenHeight * (compHeight?.value / 100)
	} else if (isGrid) {
		const itemLayout = currentLayoutData?.itemLayout as GridItemLayout
		const currGridArea = itemLayout.gridArea
		const containerLayoutData = layoutData[parentId.replace('_relative', '')] as SingleLayoutData
		const containerLayout = containerLayoutData?.containerLayout as GridContainerLayout
		const rowsArray = containerLayout?.rows

		const { rowStart, rowEnd } = currGridArea as ResponsiveGridArea
		const availableHeight = parentHeight
		const heightByContainer = rowsArray?.[(rowStart as number) - 1] as MinMaxSize | UnitSize

		height = availableHeight
		if ((heightByContainer as MinMaxSize)?.min?.type === 'vw') {
			const rowsHeight = rowsArray?.reduce(
				(heightAcc: number, rowInContainer) =>
					heightAcc + ((rowInContainer as MinMaxSize)?.min as UnitSize)?.value || 0,
				0
			)
			height = viewPortWidth * (rowsHeight / 100)
		}
		if (heightByContainer) {
			if (heightByContainer.type === 'fr') {
				const availableHeightUnits = rowsArray?.reduce?.((acc, rowUnit) => acc + (rowUnit as UnitSize).value, 0)
				const compHeightUnits = rowsArray?.reduce?.((acc, rowUnit, index) => {
					const rowIndex = index + 1
					return rowIndex >= rowStart && rowIndex <= rowEnd ? acc + (rowUnit as UnitSize).value : acc
				}, 0)

				height = availableHeight * (compHeightUnits / availableHeightUnits)

				if (compHeight?.type === 'percentage') {
					height = ((height || 0) * compHeight.value) / 100
				}
			} else {
				height = availableHeight
			}
		}
	} else if (compHeight?.type === 'percentage') {
		// todo type auto siblings should get the rest I think
		height = (parentHeight * compHeight.value) / 100
	} else {
		for (const childId of component.components || []) {
			const childComponent = componentsMap[childId]
			if (childComponent) {
				calculatedDimensions = calculateHeight(
					childComponent,
					layoutData,
					componentsMap,
					calculatedDimensions,
					component.id,
					viewPortWidth,
					parentHeight
				)
				height += calculatedDimensions[childId]?.height || 0
			}
		}
	}

	const minHeight = currentLayoutData?.componentLayout?.minHeight
	if (minHeight?.type === 'px' && minHeight?.value > height) {
		height = minHeight?.value
	}

	calculatedDimensions[component.id].height = height
	calculatedDimensions[component.id].availableInnerHeight = getAvailableInnerHeight(
		component.id,
		parentId,
		height,
		layoutData,
		calculatedDimensions
	)

	return calculatedDimensions
}
